import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SelectInput from '@/components/General/inputs/SelectInput';
import InputSection from '@/components/General/inputs/InputSection';
import { createClassInstance, updateClassInstance } from '@/data/training';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toast from 'react-native-toast-message';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

export default function EditClassInstanceModal() {
    const { id, moduleID } = useLocalSearchParams();
    const isEditing = !!id;

    const queryClient = useQueryClient();
    const router = useRouter();

    const { data: trainingModule } = Query.trainingModules.byId({ id: moduleID, options: { enabled: !!moduleID } });
    const { data: instanceToEdit, isLoading } = Query.trainingClassInstances.byId({ id, options: { enabled: isEditing } });

    const [selectedClassID, setSelectedClassID] = useState(null);
    const [scheduledAt, setScheduledAt] = useState(new Date());
    const [notes, setNotes] = useState('');
    const [teacherID, setTeacherID] = useState(null);

    const allClasses = trainingModule?.Classes || [];
    const teacherOptions = (trainingModule?.Teachers || []).map(t => ({
        label: `${t.firstName} ${t.lastName}`,
        value: t.id,
    }));

    useEffect(() => {
        if (!isLoading && instanceToEdit) {
            setSelectedClassID(instanceToEdit.classID);
            setScheduledAt(new Date(instanceToEdit.scheduledAt));
            setNotes(instanceToEdit.notes || '');
            setTeacherID(instanceToEdit.teacherID || null);
        }
    }, [isLoading, instanceToEdit]);

    const saveMutation = useMutation({
        mutationFn: isEditing ? updateClassInstance : createClassInstance,
        onSuccess: () => {
            Toast.show({
                type: 'success',
                text2: isEditing ? 'Class instance updated successfully!' : 'Class instance scheduled successfully!'
            });
            queryClient.invalidateQueries({ queryKey: QueryKeys.trainingModules.byId(moduleID) });
            queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
            if (isEditing) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.trainingClassInstances.byId(id) });
            }
            router.dismiss?.() || router.back();
        }
    });

    const handleSave = () => {
        if (saveMutation.isPending) return;
        if (!selectedClassID) {
            Toast.show({ type: 'error', text2: 'Please select a class' });
            return;
        }

        if (isEditing) {
            saveMutation.mutate({
                instanceID: id,
                moduleID,
                classID: selectedClassID,
                scheduledAt: scheduledAt.toISOString(),
                notes,
                teacherID: teacherID || null,
                termID: instanceToEdit?.termID || null,
            });
        } else {
            saveMutation.mutate({
                moduleID,
                classID: selectedClassID,
                scheduledAt: scheduledAt.toISOString(),
                notes,
                teacherID: teacherID || null,
            });
        }
    };

    return (
        <ModalWrapper
            title={isEditing ? "Edit Class Instance" : "Schedule Class Instance"}
            onConfirm={handleSave}
            loading={saveMutation.isPending}
        >
            <KeyboardAwareScrollView
                contentContainerStyle={styles.container}
                keyboardShouldPersistTaps="handled"
            >
                <InputSection title="Class Details">
                    <SelectInput
                        title="Class"
                        value={selectedClassID}
                        options={allClasses.map(c => ({label: c.name, value: c.id}))}
                        onUpdate={(value) => setSelectedClassID(value)}
                    />
                    <DatePickerInput
                        title="Date & Time"
                        mode="datetime"
                        value={scheduledAt}
                        onUpdate={setScheduledAt}
                    />
                    <SelectInput
                        title="Teacher (optional)"
                        placeholder="No teacher assigned"
                        value={teacherID}
                        options={teacherOptions}
                        onUpdate={(value) => setTeacherID(value || null)}
                    />
                    <Input
                        title="Notes (optional)"
                        multiline
                        value={notes}
                        onUpdate={setNotes}
                    />
                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing }) => ({
    container: {
        gap: spacing.md,
    },
    buttonContainer: {
        marginTop: spacing.lg,
        gap: spacing.sm,
    },
    deleteButton: {
        backgroundColor: colors.errorContainer,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        borderRadius: 8,
        alignItems: 'center',
    },
    deleteButtonText: {
        color: colors.error,
        fontFamily: fonts.medium,
        fontSize: 16,
    },
}));
